import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { io, type Socket } from 'socket.io-client';
import { getSessionToken, subscribeSessionToken } from './api.js';
import {
  realtimeInvalidationKeys,
  realtimeRecoveryKeys,
  type RealtimeEvent,
} from './realtime-invalidation.js';

export function RealtimeBridge() {
  const queryClient = useQueryClient();
  const token = useSyncExternalStore(subscribeSessionToken, getSessionToken, getSessionToken);
  const location = useLocation();
  const socketRef = useRef<Socket | null>(null);
  const activeConversation = /^\/chat\/([^/]+)$/.exec(location.pathname)?.[1] ?? null;

  useEffect(() => {
    if (!token) return;
    const socket: Socket = io({
      path: '/api/v1/realtime',
      transports: ['websocket'],
      auth: { token },
    });
    socketRef.current = socket;
    const refreshConversations = () => {
      for (const queryKey of realtimeRecoveryKeys())
        void queryClient.invalidateQueries({ queryKey });
    };
    const recoverAfterVisibilityResume = () => {
      if (document.visibilityState !== 'visible') return;
      socket.connect();
      refreshConversations();
    };
    const onEvent = (event: RealtimeEvent) => {
      for (const queryKey of realtimeInvalidationKeys(event))
        void queryClient.invalidateQueries({ queryKey });
    };
    socket.on('sync.required', refreshConversations);
    document.addEventListener('visibilitychange', recoverAfterVisibilityResume);
    socket.on('event', onEvent);
    return () => {
      socket.off('sync.required', refreshConversations);
      socket.off('event', onEvent);
      document.removeEventListener('visibilitychange', recoverAfterVisibilityResume);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [queryClient, token]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;
    const publish = () =>
      socket.emit('active.conversation', { conversationId: activeConversation });
    socket.on('connect', publish);
    publish();
    const heartbeat = window.setInterval(publish, 20_000);
    return () => {
      window.clearInterval(heartbeat);
      socket.off('connect', publish);
      socket.emit('active.conversation', { conversationId: null });
    };
  }, [activeConversation, token]);

  return null;
}
